import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import { api, nairaFormat, calcEarnings } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast, Toaster } from "sonner";
import { CalendarIcon, CheckCircle2, ExternalLink, Trash2, Plus, Send, Megaphone, Users, Building2, Banknote, BadgeCheck, Download, FileSpreadsheet, Eye, EyeOff, Settings, UserCog, Image as ImageIcon, Pencil, BarChart3 } from "lucide-react";
import ImageUploader, { imgUrl } from "@/components/ImageUploader";

const STATUS_STYLE = {
  open: "bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40",
  in_progress: "bg-[#F4B41A]/20 text-[#F4B41A] border-[#F4B41A]/40",
  proof_submitted: "bg-[#F4B41A]/20 text-[#F4B41A] border-[#F4B41A]/40",
  verified: "bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40",
  paid: "bg-[#0D2114] text-[#00E676] border-[#00E676]",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [gigs, setGigs] = useState([]);
  const [clients, setClients] = useState([]);
  const [roster, setRoster] = useState([]);

  const loadAll = async () => {
    try {
      const [s, g, c, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/gigs"),
        api.get("/admin/clients"),
        api.get("/admin/roster"),
      ]);
      setStats(s.data);
      setGigs(g.data || []);
      setClients(c.data || []);
      setRoster(r.data || []);
    } catch {}
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <div className="min-h-screen bg-[#030A06] pb-20">
      <Header />
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-10">
          <span className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#F4B41A]">// Admin · Control Room</span>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Nasarawa Expo HQ</h1>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#122E1C] mb-10">
          <Kpi icon={<Users className="w-4 h-4" />} label="Creatives" value={stats.total_creatives ?? "—"} />
          <Kpi icon={<Megaphone className="w-4 h-4" />} label="Open gigs" value={stats.open_gigs ?? "—"} accent />
          <Kpi icon={<CheckCircle2 className="w-4 h-4" />} label="In progress" value={stats.in_progress ?? "—"} />
          <Kpi icon={<Banknote className="w-4 h-4" />} label="Treasury (20%)" value={nairaFormat(stats.agency_treasury_ngn || 0)} gold />
          <Kpi icon={<BadgeCheck className="w-4 h-4" />} label="Paid to creators" value={nairaFormat(stats.creative_paid_total_ngn || 0)} />
        </div>

        <Tabs defaultValue="gigs" className="w-full">
          <div className="overflow-x-auto mb-8 -mx-6 px-6 md:mx-0 md:px-0">
            <TabsList className="bg-transparent border border-[#122E1C] rounded-none h-auto p-0 inline-flex w-max">
              {[
                ["gigs", "Gigs"], ["clients", "Clients"], ["roster", "Roster"], ["users", "All users"], ["listings", "Listings"], ["reports", "Reports"], ["payouts", "Payouts"], ["broadcast", "Broadcast"], ["cms", "Site CMS"],
              ].map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-none data-[state=active]:bg-[#F4B41A] data-[state=active]:text-[#030A06] text-[#8A9F90] text-xs uppercase tracking-[0.2em] font-mono-jb px-4 md:px-6 py-3 whitespace-nowrap">
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* GIGS */}
          <TabsContent value="gigs" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-display font-bold text-2xl tracking-tight">Gig pipeline</h2>
              <CreateGigDialog clients={clients} onCreated={loadAll} />
            </div>
            <div className="space-y-3">
              {gigs.length === 0 && <div className="border border-dashed border-[#122E1C] p-12 text-center text-[#8A9F90]">No gigs yet. Click "New gig" to post your first brief.</div>}
              {gigs.map((g) => <AdminGigRow key={g.id} gig={g} onChanged={loadAll} />)}
            </div>
          </TabsContent>

          {/* CLIENTS */}
          <TabsContent value="clients" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-display font-bold text-2xl tracking-tight">Clients</h2>
              <CreateClientDialog onCreated={loadAll} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {clients.length === 0 && <div className="md:col-span-2 border border-dashed border-[#122E1C] p-12 text-center text-[#8A9F90]">No clients added.</div>}
              {clients.map((c) => (
                <div key={c.id} data-testid={`client-card-${c.id}`} className="border border-[#122E1C] bg-[#07140B] p-5 lift">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-display font-bold text-lg tracking-tight">{c.name}</h3>
                      <div className="text-xs text-[#8A9F90] font-mono-jb mt-1">{c.contact}</div>
                      {c.notes && <p className="text-sm text-[#8A9F90] mt-3">{c.notes}</p>}
                    </div>
                    <button onClick={async () => { await api.delete(`/admin/clients/${c.id}`); loadAll(); }} data-testid={`client-delete-${c.id}`} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ROSTER */}
          <TabsContent value="roster">
            <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Master roster</h2>

            {/* Mobile-only stacked cards */}
            <div className="md:hidden space-y-3">
              {roster.length === 0 && <div className="border border-dashed border-[#122E1C] p-8 text-center text-[#8A9F90] text-sm">No creatives registered yet.</div>}
              {roster.map((r) => (
                <div key={r.id} data-testid={`roster-card-${r.id}`} className="border border-[#122E1C] bg-[#07140B] p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{r.name}</div>
                      <div className="text-xs text-[#8A9F90] break-all">{r.email}</div>
                      {r.phone && <div className="text-xs text-[#8A9F90] font-mono-jb">{r.phone}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-jb text-[#00E676] font-bold">{(r.total_followers || 0).toLocaleString()}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#8A9F90] font-mono-jb">followers</div>
                    </div>
                  </div>
                  {(r.social_handles || []).length > 0 && (
                    <div className="text-xs space-y-1 pt-2 border-t border-[#122E1C]">
                      {r.social_handles.map((h, i) => (
                        <div key={i} className="flex items-center gap-2 flex-wrap">
                          <span className="text-[#8A9F90] uppercase font-mono-jb text-[10px]">{h.platform}</span>
                          {h.url ? <a href={h.url} target="_blank" rel="noreferrer" className="text-[#00E676] hover:underline break-all">{h.handle}</a> : <span className="break-all">{h.handle}</span>}
                          <span className="text-[#F4B41A] font-mono-jb">({Number(h.followers || 0).toLocaleString()})</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-[#122E1C]">
                    {r.verified ? (
                      <Badge className="bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 rounded-none text-[10px]">VERIFIED</Badge>
                    ) : (
                      <button onClick={async () => { await api.post(`/admin/creatives/${r.id}/verify`); toast.success("Verified"); loadAll(); }} data-testid={`verify-creative-${r.id}`} className="text-[#00E676] text-xs underline">Verify</button>
                    )}
                    <NotifyCreativeButton creative={r} />
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block border border-[#122E1C] overflow-x-auto">
              <div className="grid grid-cols-12 bg-[#0D2114] text-[10px] uppercase tracking-[0.2em] font-mono-jb text-[#8A9F90] min-w-[820px]">
                <div className="p-3 col-span-3">Name</div>
                <div className="p-3 col-span-3">Email · Phone</div>
                <div className="p-3 col-span-3">Handles</div>
                <div className="p-3 col-span-2 text-right">Followers</div>
                <div className="p-3 col-span-1"></div>
              </div>
              {roster.length === 0 && <div className="p-8 text-center text-[#8A9F90] text-sm">No creatives registered yet.</div>}
              {roster.map((r) => (
                <div key={r.id} data-testid={`roster-row-${r.id}`} className="grid grid-cols-12 border-t border-[#122E1C] min-w-[820px] hover:bg-[#0D2114]/40 items-center">
                  <div className="p-3 col-span-3">
                    <div className="font-semibold">{r.name}</div>
                    {r.verified && <Badge className="bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 rounded-none text-[10px] mt-1">VERIFIED</Badge>}
                  </div>
                  <div className="p-3 col-span-3 text-xs">
                    <div className="break-all">{r.email}</div>
                    <div className="text-[#8A9F90]">{r.phone}</div>
                  </div>
                  <div className="p-3 col-span-3 text-xs space-y-1">
                    {(r.social_handles || []).map((h, i) => (
                      <div key={i} className="flex items-center gap-1 flex-wrap">
                        <span className="text-[#8A9F90] uppercase font-mono-jb">{h.platform}:</span>
                        {h.url ? <a href={h.url} target="_blank" rel="noreferrer" className="text-[#00E676] hover:underline">{h.handle}</a> : <span>{h.handle}</span>}
                        <span className="text-[#F4B41A] font-mono-jb">({Number(h.followers || 0).toLocaleString()})</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 col-span-2 text-right font-mono-jb text-[#00E676] font-bold">{(r.total_followers || 0).toLocaleString()}</div>
                  <div className="p-3 col-span-1 text-right flex flex-col gap-1 items-end">
                    {!r.verified && (
                      <button onClick={async () => { await api.post(`/admin/creatives/${r.id}/verify`); toast.success("Verified"); loadAll(); }} data-testid={`verify-creative-${r.id}`} className="text-[#00E676] text-xs underline">Verify</button>
                    )}
                    <NotifyCreativeButton creative={r} />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users">
            <AllUsersTable />
          </TabsContent>

          {/* LISTINGS (toggle public visibility) */}
          <TabsContent value="listings">
            <ListingsManager />
          </TabsContent>

          {/* CMS */}
          <TabsContent value="cms">
            <SiteCmsEditor />
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports">
            <ReportsPanel />
          </TabsContent>

          {/* PAYOUTS */}
          <TabsContent value="payouts">
            <PayoutsExport stats={stats} />
          </TabsContent>

          {/* BROADCAST */}
          <TabsContent value="broadcast">
            <BroadcastForm onSent={() => toast.success("Broadcast sent to all creatives")} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, accent, gold }) {
  return (
    <div className="bg-[#030A06] p-5">
      <div className="flex items-center gap-2 text-[#8A9F90]">{icon}<span className="text-[10px] uppercase tracking-[0.2em] font-mono-jb">{label}</span></div>
      <div className={`font-mono-jb text-2xl mt-2 ${accent ? "text-[#00E676]" : gold ? "text-[#F4B41A]" : "text-white"}`}>{value}</div>
    </div>
  );
}

const FOLLOWER_RANGES = [
  { value: "1000", label: "1,000 – 5,000", min: 1000, max: 5000 },
  { value: "5000", label: "5,000 – 10,000", min: 5000, max: 10000 },
  { value: "10000", label: "10,000 – 20,000", min: 10000, max: 20000 },
  { value: "20000", label: "20,000 – 50,000", min: 20000, max: 50000 },
  { value: "50000", label: "50,000 – 100,000", min: 50000, max: 100000 },
  { value: "100000", label: "100,000 – 500,000", min: 100000, max: 500000 },
  { value: "500000", label: "500,000 – 1,000,000", min: 500000, max: 1000000 },
  { value: "1000000", label: "1,000,000 – 5,000,000", min: 1000000, max: 5000000 },
  { value: "custom", label: "Custom number…" },
];

function CreateGigDialog({ clients, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", brief: "", client_name: "", required_followers: 5000, follower_range: "5000", follower_range_label: "5,000 – 10,000", custom_min: 1000, custom_max: 5000, budget_ngn: 2500, platforms: ["tiktok", "instagram"], deadline: "", notes: "", is_special: false });
  const [busy, setBusy] = useState(false);

  const grossSuggest = calcEarnings(form.required_followers).gross;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        budget_ngn: Number(form.budget_ngn || (form.is_special ? 0 : grossSuggest)),
        required_followers: form.is_special ? 0 : Number(form.required_followers),
        follower_range_label: form.is_special ? "Special · open to all creatives" : form.follower_range_label,
      };
      await api.post("/gigs", payload);
      toast.success("Gig posted · creatives notified");
      setOpen(false);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-gig-btn" className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
          <Plus className="w-4 h-4 mr-2" /> New gig
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Post a new gig</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <FormField label="Title" testid="gig-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <FormField label="Client" testid="gig-client" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} list={clients.map((c) => c.name)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Brief</Label>
            <Textarea data-testid="gig-brief" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" rows={4} />
          </div>

          {/* Special gig toggle */}
          <label htmlFor="gig-is-special" className="flex items-start gap-3 cursor-pointer border border-[#F4B41A]/40 bg-[#F4B41A]/5 p-3 select-none">
            <input
              id="gig-is-special"
              data-testid="gig-is-special"
              type="checkbox"
              checked={form.is_special}
              onChange={(e) => setForm({ ...form, is_special: e.target.checked })}
              className="mt-0.5 accent-[#F4B41A] w-4 h-4"
            />
            <div className="flex-1">
              <div className="font-mono-jb text-xs uppercase tracking-widest text-[#F4B41A]">★ Special gig</div>
              <div className="text-xs text-[#8A9F90] mt-1">No follower-count requirement — any registered creative can accept. Use this for raffles, voice-overs, community engagement, paid surveys, etc.</div>
            </div>
          </label>
          <div className="grid md:grid-cols-3 gap-3">
            {!form.is_special && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Required follower range</Label>
              <select
                data-testid="gig-followers-range"
                value={form.follower_range}
                onChange={(e) => {
                  const val = e.target.value;
                  const opt = FOLLOWER_RANGES.find((r) => r.value === val);
                  if (val === "custom") {
                    setForm({ ...form, follower_range: "custom", follower_range_label: "" });
                  } else {
                    const newGross = calcEarnings(opt.min).gross;
                    setForm({
                      ...form,
                      follower_range: val,
                      follower_range_label: opt.label,
                      required_followers: opt.min,
                      budget_ngn: newGross,
                    });
                  }
                }}
                className="mt-2 w-full bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none focus:border-[#00E676] outline-none font-mono-jb"
              >
                {FOLLOWER_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {form.follower_range === "custom" && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Min (≥ 1,000)</Label>
                      <Input
                        data-testid="gig-custom-min"
                        type="number"
                        min={1000}
                        max={1000000}
                        step={500}
                        placeholder="1,000"
                        value={form.custom_min}
                        onChange={(e) => {
                          const min = Math.max(1000, Math.min(1000000, Number(e.target.value || 0)));
                          const max = Math.max(min, form.custom_max);
                          setForm({
                            ...form,
                            custom_min: min,
                            custom_max: max,
                            required_followers: min,
                            budget_ngn: calcEarnings(min).gross,
                            follower_range_label: `${min.toLocaleString()} – ${max.toLocaleString()}`,
                          });
                        }}
                        className="bg-transparent border-[#122E1C] rounded-none mt-1 text-white font-mono-jb"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Max (≤ 1,000,000)</Label>
                      <Input
                        data-testid="gig-custom-max"
                        type="number"
                        min={1000}
                        max={1000000}
                        step={500}
                        placeholder="1,000,000"
                        value={form.custom_max}
                        onChange={(e) => {
                          const max = Math.max(form.custom_min, Math.min(1000000, Number(e.target.value || 0)));
                          setForm({
                            ...form,
                            custom_max: max,
                            follower_range_label: `${Number(form.custom_min).toLocaleString()} – ${max.toLocaleString()}`,
                          });
                        }}
                        className="bg-transparent border-[#122E1C] rounded-none mt-1 text-white font-mono-jb"
                      />
                    </div>
                  </div>
                  {form.custom_min > form.custom_max && (
                    <div className="text-[10px] text-[#FF3B30] font-mono-jb">Max must be greater than min.</div>
                  )}
                  <div className="text-[10px] text-[#8A9F90] font-mono-jb">
                    Range: {Number(form.custom_min).toLocaleString()} – {Number(form.custom_max).toLocaleString()} followers
                  </div>
                </div>
              )}
            </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">{form.is_special ? "Budget (₦)" : `Budget (₦) · auto: ${grossSuggest}`}</Label>
              <Input data-testid="gig-budget" type="number" value={form.budget_ngn} onChange={(e) => setForm({ ...form, budget_ngn: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white font-mono-jb" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Deadline</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="gig-deadline-trigger" type="button" className="mt-2 w-full bg-transparent border border-[#122E1C] text-white p-2 text-left text-sm flex items-center gap-2 rounded-none hover:border-[#00E676]/50">
                    <CalendarIcon className="w-4 h-4" /> {form.deadline || "Pick date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="bg-[#07140B] border-[#122E1C] p-0">
                  <Calendar mode="single" selected={form.deadline ? new Date(form.deadline) : undefined} onSelect={(d) => setForm({ ...form, deadline: d ? d.toISOString().slice(0, 10) : "" })} className="bg-[#07140B] text-white" />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Platforms (comma-separated)</Label>
            <Input data-testid="gig-platforms" value={form.platforms.join(", ")} onChange={(e) => setForm({ ...form, platforms: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
          </div>
          <div className="border border-[#00E676]/30 bg-[#0D2114] p-4 grid grid-cols-3 gap-3 text-sm font-mono-jb">
            <div><div className="text-[10px] text-[#8A9F90] uppercase tracking-widest">Creative (80%)</div><div className="text-[#00E676] mt-1">{nairaFormat(Math.round(Number(form.budget_ngn || grossSuggest) * 0.8))}</div></div>
            <div><div className="text-[10px] text-[#8A9F90] uppercase tracking-widest">Treasury (20%)</div><div className="text-[#F4B41A] mt-1">{nairaFormat(Math.round(Number(form.budget_ngn || grossSuggest) * 0.2))}</div></div>
            <div><div className="text-[10px] text-[#8A9F90] uppercase tracking-widest">Gross</div><div className="text-white mt-1">{nairaFormat(Number(form.budget_ngn || grossSuggest))}</div></div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="gig-create-submit" disabled={busy || !form.title || !form.brief || (form.follower_range === "custom" && form.custom_min > form.custom_max)} onClick={submit} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">Post gig & notify creatives</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, testid, value, onChange, list }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">{label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} list={list ? `${testid}-list` : undefined} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
      {list && (
        <datalist id={`${testid}-list`}>
          {list.map((v) => <option key={v} value={v} />)}
        </datalist>
      )}
    </div>
  );
}

function CreateClientDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", notes: "" });

  const submit = async () => {
    try {
      await api.post("/admin/clients", form);
      toast.success("Client added");
      setOpen(false);
      setForm({ name: "", contact: "", notes: "" });
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-client-btn" className="bg-[#F4B41A] hover:bg-[#F4B41A]/80 text-[#030A06] font-bold rounded-none"><Plus className="w-4 h-4 mr-2" /> New client</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Add client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="client-name" placeholder="Client / brand name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" />
          <Input data-testid="client-contact" placeholder="Contact (phone / email)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" />
          <Textarea data-testid="client-notes" placeholder="Internal notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" />
        </div>
        <DialogFooter>
          <Button data-testid="client-create-submit" onClick={submit} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">Save client</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminGigRow({ gig, onChanged }) {
  const [openApps, setOpenApps] = useState(false);
  const [detail, setDetail] = useState(null);
  const [openPayout, setOpenPayout] = useState(false);
  const [transferRef, setTransferRef] = useState("");

  const loadDetail = async () => {
    const { data } = await api.get(`/gigs/${gig.id}`);
    setDetail(data);
  };

  const select = async (creativeId) => {
    await api.post(`/gigs/${gig.id}/select`, { creative_id: creativeId });
    toast.success("Creative selected · they were notified");
    await loadDetail();
    onChanged();
  };

  const verify = async () => {
    await api.post(`/gigs/${gig.id}/verify`);
    toast.success("Proof verified");
    onChanged();
  };

  const payout = async () => {
    await api.post(`/gigs/${gig.id}/payout`, { transfer_reference: transferRef });
    toast.success("Payout marked · creative notified");
    setOpenPayout(false);
    setTransferRef("");
    onChanged();
  };

  const remove = async () => {
    if (!window.confirm("Delete this gig?")) return;
    await api.delete(`/gigs/${gig.id}`);
    toast.success("Gig deleted");
    onChanged();
  };

  return (
    <div data-testid={`admin-gig-row-${gig.id}`} className="border border-[#122E1C] bg-[#07140B] p-5 lift">
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${STATUS_STYLE[gig.status]} border rounded-none uppercase font-mono-jb text-[10px]`}>{gig.status.replace("_", " ")}</Badge>
            {gig.is_special && <Badge className="bg-[#F4B41A]/20 text-[#F4B41A] border border-[#F4B41A]/40 rounded-none uppercase font-mono-jb text-[10px]">★ Special</Badge>}
            <span className="text-xs text-[#8A9F90]">{gig.client_name}</span>
          </div>
          <h3 className="font-display font-bold text-xl tracking-tight">{gig.title}</h3>
          <p className="text-sm text-[#8A9F90] mt-1 line-clamp-2">{gig.brief}</p>
          <div className="flex gap-4 text-[11px] font-mono-jb text-[#8A9F90] mt-2">
            <span>{gig.is_special ? "Open to all creatives" : (gig.follower_range_label || `${gig.required_followers?.toLocaleString()}+ reach`)}</span>
            <span>· {gig.platforms?.join(", ")}</span>
            {gig.deadline && <span>· by {gig.deadline}</span>}
          </div>
        </div>

        <div className="text-right space-y-1 min-w-[160px]">
          <div className="font-mono-jb text-xs text-[#8A9F90]">Creative · 80%</div>
          <div className="font-mono-jb text-[#00E676] text-xl">{nairaFormat(gig.creative_share_ngn)}</div>
          <div className="font-mono-jb text-xs text-[#F4B41A]">Treasury: {nairaFormat(gig.agency_share_ngn)}</div>

          <div className="flex flex-wrap gap-2 justify-end mt-3">
            {gig.status !== "paid" && (
              <Dialog open={openApps} onOpenChange={(o) => { setOpenApps(o); if (o) loadDetail(); }}>
                <DialogTrigger asChild>
                  <Button
                    data-testid={`view-applicants-${gig.id}`}
                    size="sm"
                    variant={gig.status === "open" ? "default" : "outline"}
                    className={
                      gig.status === "open"
                        ? "bg-[#F4B41A] hover:bg-[#F4B41A]/80 text-[#030A06] font-bold rounded-none"
                        : "border-[#122E1C] hover:border-[#F4B41A] hover:bg-[#0D2114] text-[#F4B41A] rounded-none"
                    }
                  >
                    {gig.status === "open" ? "View applicants" : "Applicants"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
                  <DialogHeader><DialogTitle className="font-display tracking-tight">Applicants for "{gig.title}"</DialogTitle></DialogHeader>
                  {!detail && <div className="text-[#8A9F90] text-sm">Loading…</div>}
                  {detail && detail.applications?.length === 0 && <div className="text-[#8A9F90] text-sm">No applicants yet.</div>}
                  {detail && detail.applications?.map((a) => (
                    <div key={a.id} data-testid={`applicant-${a.id}`} className={`border p-4 ${a.status === "declined" || a.status === "rejected" ? "border-[#FF3B30]/30 bg-[#FF3B30]/5 opacity-70" : "border-[#122E1C]"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{a.creative?.name}</span>
                            <Badge className={`rounded-none uppercase tracking-widest text-[10px] font-mono-jb border ${
                              a.status === "selected" ? "bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40" :
                              a.status === "paid" ? "bg-[#00E676]/30 text-[#00E676] border-[#00E676]" :
                              a.status === "declined" ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" :
                              a.status === "rejected" ? "bg-[#0D2114] text-[#8A9F90] border-[#122E1C]" :
                              "bg-[#F4B41A]/20 text-[#F4B41A] border-[#F4B41A]/40"
                            }`}>
                              {a.status === "pending" ? "Accepted" : a.status}
                            </Badge>
                          </div>
                          <div className="text-xs text-[#8A9F90] font-mono-jb mt-1">{(a.creative?.total_followers || 0).toLocaleString()} combined followers · {a.creative?.phone || "no phone"}</div>
                          {a.message && <p className="text-xs text-[#8A9F90] mt-2 italic">"{a.message}"</p>}
                          <div className="mt-2 text-xs space-y-0.5">
                            {(a.creative?.social_handles || []).map((h, i) => (
                              <div key={i}>{h.url ? <a href={h.url} target="_blank" rel="noreferrer" className="text-[#00E676] hover:underline">{h.platform}: {h.handle}</a> : <span>{h.platform}: {h.handle}</span>} <span className="text-[#F4B41A] font-mono-jb">({Number(h.followers || 0).toLocaleString()})</span></div>
                            ))}
                          </div>
                        </div>
                        {a.status !== "declined" && a.status !== "rejected" && a.status !== "paid" && (
                          <div className="flex flex-col gap-2 shrink-0">
                            <Button data-testid={`select-applicant-${a.id}`} size="sm" onClick={() => select(a.creative_id)} disabled={a.status === "selected"} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none disabled:opacity-60">
                              {a.status === "selected" ? "✓ Selected" : "Select"}
                            </Button>
                            {a.status !== "selected" && (
                              <Button data-testid={`reject-applicant-${a.id}`} size="sm" variant="outline"
                                onClick={async () => {
                                  if (!window.confirm(`Reject ${a.creative?.name}?`)) return;
                                  await api.post(`/gigs/${gig.id}/reject/${a.creative_id}`);
                                  toast("Applicant rejected");
                                  loadDetail(); onChanged();
                                }}
                                className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] rounded-none text-xs">
                                Reject
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Selected/paid: show their proof of work + bank details + payout action */}
                      {(a.status === "selected" || a.status === "paid") && (
                        <div className="mt-4 pt-4 border-t border-[#122E1C] space-y-3">
                          {(a.proof_links?.length > 0) ? (
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-jb text-[#F4B41A] mb-1">Proof of work · submitted {a.proof_submitted_at ? new Date(a.proof_submitted_at).toLocaleDateString() : ""}</div>
                              {a.proof_links.map((l, i) => (
                                <a key={i} href={l} target="_blank" rel="noreferrer" data-testid={`proof-link-${a.id}-${i}`} className="text-[#00E676] text-xs hover:underline flex items-center gap-1 break-all"><ExternalLink className="w-3 h-3 shrink-0" /> {l}</a>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-[#8A9F90] italic">Awaiting proof of work…</div>
                          )}

                          <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-mono-jb text-[#F4B41A] mb-1">Bank details for payout</div>
                            {a.creative?.bank_details ? (
                              <div className="grid grid-cols-3 gap-2 text-xs bg-[#0D2114] p-2">
                                <div><div className="text-[#8A9F90] uppercase text-[9px] tracking-widest font-mono-jb">Bank</div><div className="font-mono-jb">{a.creative.bank_details.bank_name || "—"}</div></div>
                                <div><div className="text-[#8A9F90] uppercase text-[9px] tracking-widest font-mono-jb">Account #</div><div className="font-mono-jb text-[#00E676] flex items-center gap-1">{a.creative.bank_details.account_number || "—"} {a.creative.bank_details.account_number && (<button onClick={() => { navigator.clipboard.writeText(a.creative.bank_details.account_number); toast.success("Copied"); }} className="text-[#F4B41A] hover:underline text-[9px]">copy</button>)}</div></div>
                                <div><div className="text-[#8A9F90] uppercase text-[9px] tracking-widest font-mono-jb">Name</div><div className="font-mono-jb">{a.creative.bank_details.account_name || "—"}</div></div>
                              </div>
                            ) : (
                              <div className="text-xs text-[#FF3B30]">Creative hasn't added bank details yet.</div>
                            )}
                          </div>

                          {a.status === "selected" && (
                            <ApplicantPayoutButton gigId={gig.id} applicant={a} onDone={() => { loadDetail(); onChanged(); }} amount={gig.creative_share_ngn} hasProof={a.proof_links?.length > 0} gig={gig} />
                          )}
                          {a.status === "paid" && (
                            <div className="text-xs text-[#00E676] font-mono-jb">
                              ✓ Paid {a.payout_marked_at ? new Date(a.payout_marked_at).toLocaleDateString() : ""}
                              {a.transfer_reference && ` · Ref ${a.transfer_reference}`}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </DialogContent>
              </Dialog>
            )}

            {gig.status !== "paid" && <EditGigButton gig={gig} onSaved={onChanged} />}
            {gig.status === "open" && (
              <button
                onClick={async () => { if (window.confirm("Stop accepting new applications on this gig?")) { await api.post(`/gigs/${gig.id}/close`); toast.success("Gig closed to new applications"); onChanged(); } }}
                data-testid={`close-gig-${gig.id}`}
                className="text-[#F4B41A] hover:bg-[#F4B41A]/10 p-2 text-xs font-mono-jb"
              >
                Close
              </button>
            )}
            {gig.status === "closed" && (
              <button
                onClick={async () => { await api.post(`/gigs/${gig.id}/reopen`); toast.success("Gig reopened"); onChanged(); }}
                data-testid={`reopen-gig-${gig.id}`}
                className="text-[#00E676] hover:bg-[#00E676]/10 p-2 text-xs font-mono-jb"
              >
                Reopen
              </button>
            )}
            <button onClick={remove} data-testid={`delete-gig-${gig.id}`} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
function AllUsersTable() {
  const [items, setItems] = useState([]);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    const params = {};
    if (role !== "all") params.role = role;
    const { data } = await api.get("/admin/users", { params });
    setItems(data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const filtered = items.filter((u) => !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">All registrations</h2>
          <p className="text-sm text-[#8A9F90] mt-1">Everyone who has ever registered on Nasarawa Expo.</p>
        </div>
        <div className="flex gap-2">
          <Input data-testid="users-search" placeholder="Search name/email" value={q} onChange={(e) => setQ(e.target.value)} className="bg-transparent border-[#122E1C] rounded-none text-white w-56" />
          <select data-testid="users-role-filter" value={role} onChange={(e) => setRole(e.target.value)} className="bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none">
            <option value="all">All roles</option>
            <option value="creative">Creatives</option>
            <option value="admin">Admins</option>
          </select>
        </div>
      </div>
      <div className="border border-[#122E1C] overflow-x-auto">
        <div className="grid grid-cols-12 bg-[#0D2114] text-[10px] uppercase tracking-[0.2em] font-mono-jb text-[#8A9F90] min-w-[820px]">
          <div className="p-3 col-span-3">Name</div>
          <div className="p-3 col-span-3">Email</div>
          <div className="p-3 col-span-2">Phone</div>
          <div className="p-3 col-span-2">Role</div>
          <div className="p-3 col-span-2">Joined</div>
        </div>
        {filtered.length === 0 && <div className="p-8 text-center text-[#8A9F90] text-sm" data-testid="users-empty">No users found.</div>}
        {filtered.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-12 border-t border-[#122E1C] min-w-[820px] hover:bg-[#0D2114]/40 text-sm">
            <div className="p-3 col-span-3 flex items-center gap-2">
              <div className="w-6 h-6 bg-[#0D2114] flex items-center justify-center text-xs font-mono-jb text-[#00E676]">{u.name?.[0]?.toUpperCase() || "?"}</div>
              {u.name}
            </div>
            <div className="p-3 col-span-3 text-xs">{u.email}</div>
            <div className="p-3 col-span-2 text-xs text-[#8A9F90]">{u.phone || "—"}</div>
            <div className="p-3 col-span-2">
              <Badge className={`rounded-none uppercase font-mono-jb text-[10px] ${u.role === "admin" ? "bg-[#F4B41A]/20 text-[#F4B41A] border-[#F4B41A]/40" : "bg-[#00E676]/20 text-[#00E676] border-[#00E676]/40"} border`}>
                {u.role}
              </Badge>
            </div>
            <div className="p-3 col-span-2 text-xs font-mono-jb text-[#8A9F90]">{(u.created_at || "").slice(0, 10)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListingsManager() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/roster");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id, listed) => {
    setBusy(true);
    try {
      await api.post(`/admin/creatives/${id}/list`, { listed });
      toast.success(listed ? "Now listed publicly" : "Removed from public listing");
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight">Public listings</h2>
        <p className="text-sm text-[#8A9F90] mt-1">Toggle which creatives appear on the public <code className="text-[#00E676]">/creatives</code> directory.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="md:col-span-3 border border-dashed border-[#122E1C] p-12 text-center text-[#8A9F90]">No creatives yet.</div>}
        {items.map((u) => (
          <div key={u.id} data-testid={`listing-card-${u.id}`} className="border border-[#122E1C] bg-[#07140B] p-4 flex gap-3 lift">
            <div className="w-16 h-16 bg-[#030A06] border border-[#122E1C] overflow-hidden flex-shrink-0">
              {u.profile_image_path ? <img src={imgUrl(u.profile_image_path)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-display font-black text-2xl text-[#0D2114]">{u.name?.[0]?.toUpperCase()}</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{u.name}</div>
              <div className="text-[10px] text-[#8A9F90] uppercase tracking-widest font-mono-jb mt-1">{u.category || "no category"} · {(u.total_followers || 0).toLocaleString()} followers</div>
              <div className="text-[10px] text-[#8A9F90] mt-1">{(u.services || []).length} services</div>
              <div className="mt-3 flex items-center gap-2">
                {u.listed ? (
                  <Button size="sm" disabled={busy} onClick={() => toggle(u.id, false)} data-testid={`unlist-${u.id}`} className="bg-[#0D2114] text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/10 rounded-none h-7 text-xs">
                    <EyeOff className="w-3 h-3 mr-1" /> Unlist
                  </Button>
                ) : (
                  <Button size="sm" disabled={busy} onClick={() => toggle(u.id, true)} data-testid={`list-${u.id}`} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none h-7 text-xs">
                    <Eye className="w-3 h-3 mr-1" /> List publicly
                  </Button>
                )}
                <a href={`/creatives/${u.id}`} target="_blank" rel="noreferrer" className="text-[10px] text-[#8A9F90] hover:text-[#00E676] underline">Preview</a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SiteCmsEditor() {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/public/site/content");
    setContent(data || {});
  };
  useEffect(() => { load(); }, []);

  if (!content) return <div className="text-[#8A9F90]">Loading…</div>;

  const update = (k, v) => setContent({ ...content, [k]: v });
  const updateStep = (i, k, v) => {
    const next = [...(content.lifecycle_steps || [])];
    next[i] = { ...next[i], [k]: v };
    update("lifecycle_steps", next);
  };
  const updateWhy = (i, k, v) => {
    const next = [...(content.why_cards || [])];
    next[i] = { ...next[i], [k]: v };
    update("why_cards", next);
  };
  const updateTier = (i, k, v) => {
    const next = [...(content.tiers || [])];
    next[i] = { ...next[i], [k]: Number(v) || 0 };
    update("tiers", next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site/content", content);
      toast.success("Homepage updated · live now");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Homepage CMS</h2>
          <p className="text-sm text-[#8A9F90] mt-1">Every change goes live on the homepage immediately after save.</p>
        </div>
        <Button data-testid="cms-save" disabled={busy} onClick={save} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="border border-[#122E1C] bg-[#07140B] p-5 space-y-4">
        <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#00E676]">// Hero</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <CmsField label="Eyebrow text" testid="cms-hero-eyebrow" value={content.hero_eyebrow} onChange={(v) => update("hero_eyebrow", v)} />
          <CmsField label="Subtitle" testid="cms-hero-subtitle" value={content.hero_subtitle} onChange={(v) => update("hero_subtitle", v)} textarea />
          <CmsField label="Title line 1" testid="cms-hero-title-1" value={content.hero_title_1} onChange={(v) => update("hero_title_1", v)} />
          <CmsField label="Title line 2 (accent)" testid="cms-hero-title-2" value={content.hero_title_2} onChange={(v) => update("hero_title_2", v)} />
          <CmsField label="Title line 3" testid="cms-hero-title-3" value={content.hero_title_3} onChange={(v) => update("hero_title_3", v)} />
          <CmsField label="Primary CTA label" testid="cms-cta-primary" value={content.hero_cta_primary} onChange={(v) => update("hero_cta_primary", v)} />
          <CmsField label="Secondary CTA label" testid="cms-cta-secondary" value={content.hero_cta_secondary} onChange={(v) => update("hero_cta_secondary", v)} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Hero background image</Label>
          <div className="mt-2 max-w-md">
            <ImageUploader testid="cms-hero-image" label="Add hero image" currentPath={content.hero_image_path} onUploaded={(p) => update("hero_image_path", p)} onClear={() => update("hero_image_path", "")} />
          </div>
        </div>
      </div>

      <div className="border border-[#122E1C] bg-[#07140B] p-5 space-y-4">
        <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#00E676]">// Lifecycle steps</h3>
        {(content.lifecycle_steps || []).map((s, i) => (
          <div key={i} className="grid md:grid-cols-12 gap-3 border border-[#122E1C] p-3">
            <Input data-testid={`cms-step-n-${i}`} placeholder="01" value={s.n} onChange={(e) => updateStep(i, "n", e.target.value)} className="md:col-span-1 bg-transparent border-[#122E1C] rounded-none text-white font-mono-jb" />
            <Input data-testid={`cms-step-t-${i}`} placeholder="Title" value={s.t} onChange={(e) => updateStep(i, "t", e.target.value)} className="md:col-span-3 bg-transparent border-[#122E1C] rounded-none text-white" />
            <Textarea data-testid={`cms-step-d-${i}`} placeholder="Description" value={s.d} onChange={(e) => updateStep(i, "d", e.target.value)} rows={2} className="md:col-span-8 bg-transparent border-[#122E1C] rounded-none text-white" />
          </div>
        ))}
      </div>

      <div className="border border-[#122E1C] bg-[#07140B] p-5 space-y-4">
        <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#F4B41A]">// Why-us cards</h3>
        {(content.why_cards || []).map((w, i) => (
          <div key={i} className="grid md:grid-cols-12 gap-3 border border-[#122E1C] p-3">
            <select data-testid={`cms-why-icon-${i}`} value={w.icon} onChange={(e) => updateWhy(i, "icon", e.target.value)} className="md:col-span-2 bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none">
              <option value="shield">shield</option>
              <option value="bank">bank</option>
              <option value="mega">megaphone</option>
            </select>
            <Input data-testid={`cms-why-title-${i}`} placeholder="Title" value={w.title} onChange={(e) => updateWhy(i, "title", e.target.value)} className="md:col-span-3 bg-transparent border-[#122E1C] rounded-none text-white" />
            <Textarea data-testid={`cms-why-body-${i}`} placeholder="Body" value={w.body} onChange={(e) => updateWhy(i, "body", e.target.value)} rows={2} className="md:col-span-7 bg-transparent border-[#122E1C] rounded-none text-white" />
          </div>
        ))}
      </div>

      <div className="border border-[#122E1C] bg-[#07140B] p-5 space-y-3">
        <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#00E676]">// Earnings tier table</h3>
        <div className="grid grid-cols-4 text-[10px] uppercase tracking-widest text-[#8A9F90] font-mono-jb">
          <div>Followers</div><div>Gross</div><div className="text-[#00E676]">Creative</div><div className="text-[#F4B41A]">Agency</div>
        </div>
        {(content.tiers || []).map((t, i) => (
          <div key={i} className="grid grid-cols-4 gap-2">
            <Input data-testid={`cms-tier-f-${i}`} type="number" value={t.followers} onChange={(e) => updateTier(i, "followers", e.target.value)} className="bg-transparent border-[#122E1C] rounded-none text-white font-mono-jb" />
            <Input data-testid={`cms-tier-g-${i}`} type="number" value={t.gross} onChange={(e) => updateTier(i, "gross", e.target.value)} className="bg-transparent border-[#122E1C] rounded-none text-white font-mono-jb" />
            <Input data-testid={`cms-tier-c-${i}`} type="number" value={t.creative} onChange={(e) => updateTier(i, "creative", e.target.value)} className="bg-transparent border-[#122E1C] rounded-none text-[#00E676] font-mono-jb" />
            <Input data-testid={`cms-tier-a-${i}`} type="number" value={t.agency} onChange={(e) => updateTier(i, "agency", e.target.value)} className="bg-transparent border-[#122E1C] rounded-none text-[#F4B41A] font-mono-jb" />
          </div>
        ))}
      </div>

      <div className="border border-[#122E1C] bg-[#07140B] p-5 space-y-4">
        <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#F4B41A]">// Bottom CTA</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <CmsField label="CTA line 1" testid="cms-cta1" value={content.cta_title_1} onChange={(v) => update("cta_title_1", v)} />
          <CmsField label="CTA line 2 (accent)" testid="cms-cta2" value={content.cta_title_2} onChange={(v) => update("cta_title_2", v)} />
          <CmsField label="CTA subtitle" testid="cms-ctasub" value={content.cta_subtitle} onChange={(v) => update("cta_subtitle", v)} textarea />
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="cms-save-bottom" disabled={busy} onClick={save} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none px-8 py-6">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function CmsField({ label, testid, value, onChange, textarea }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">{label}</Label>
      {textarea ? (
        <Textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
      ) : (
        <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
      )}
    </div>
  );
}



function PayoutsExport({ stats }) {
  const [filter, setFilter] = useState("paid");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter && filter !== "all") params.status = filter;
      const { data } = await api.get("/gigs", { params });
      let rows = data || [];
      if (filter !== "all") rows = rows.filter((g) => g.status === filter);
      if (start) rows = rows.filter((g) => (g.payout_marked_at || "") >= start);
      if (end) rows = rows.filter((g) => (g.payout_marked_at || "") <= end + "T23:59:59");
      setPreview(rows);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [filter, start, end]);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status_filter", filter);
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const res = await api.get(`/admin/payouts/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `nasarawa-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const totals = preview.reduce(
    (acc, g) => ({
      gross: acc.gross + (g.budget_ngn || 0),
      creative: acc.creative + (g.creative_share_ngn || 0),
      agency: acc.agency + (g.agency_share_ngn || 0),
    }),
    { gross: 0, creative: 0, agency: 0 }
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Treasury reconciliation</h2>
          <p className="text-sm text-[#8A9F90] mt-1">Export paid (or any-status) gigs as a CSV with bank details, references and split breakdown.</p>
        </div>
        <Button data-testid="payouts-download-csv" onClick={downloadCsv} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
          <Download className="w-4 h-4 mr-2" /> Download CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="border border-[#122E1C] bg-[#07140B] p-5 grid md:grid-cols-4 gap-4 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Status</Label>
          <select
            data-testid="payouts-filter-status"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="mt-2 w-full bg-[#030A06] border border-[#122E1C] text-white p-2 text-sm rounded-none focus:border-[#00E676] outline-none"
          >
            <option value="paid">Paid only</option>
            <option value="verified">Verified (ready to pay)</option>
            <option value="proof_submitted">Proof submitted</option>
            <option value="in_progress">In progress</option>
            <option value="all">All statuses</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">From</Label>
          <Input data-testid="payouts-filter-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">To</Label>
          <Input data-testid="payouts-filter-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
        </div>
        <Button data-testid="payouts-clear-filters" variant="outline" onClick={() => { setFilter("paid"); setStart(""); setEnd(""); }} className="border-[#122E1C] hover:bg-[#0D2114] rounded-none">
          Reset filters
        </Button>
      </div>

      {/* Totals */}
      <div className="grid md:grid-cols-4 gap-px bg-[#122E1C]">
        <Kpi icon={<FileSpreadsheet className="w-4 h-4" />} label="Rows" value={preview.length} />
        <Kpi icon={<Banknote className="w-4 h-4" />} label="Gross" value={nairaFormat(totals.gross)} />
        <Kpi icon={<BadgeCheck className="w-4 h-4" />} label="Creatives 80%" value={nairaFormat(totals.creative)} accent />
        <Kpi icon={<Banknote className="w-4 h-4" />} label="Treasury 20%" value={nairaFormat(totals.agency)} gold />
      </div>

      {/* Preview table */}
      <div className="border border-[#122E1C] overflow-x-auto">
        <div className="grid grid-cols-12 bg-[#0D2114] text-[10px] uppercase tracking-[0.2em] font-mono-jb text-[#8A9F90] min-w-[920px]">
          <div className="p-3 col-span-2">Date</div>
          <div className="p-3 col-span-3">Gig · Client</div>
          <div className="p-3 col-span-2">Creative</div>
          <div className="p-3 col-span-2">Bank</div>
          <div className="p-3 col-span-1 text-right">Gross</div>
          <div className="p-3 col-span-1 text-right text-[#00E676]">80%</div>
          <div className="p-3 col-span-1 text-right text-[#F4B41A]">20%</div>
        </div>
        {loading && <div className="p-6 text-center text-[#8A9F90] text-sm">Loading…</div>}
        {!loading && preview.length === 0 && <div className="p-8 text-center text-[#8A9F90] text-sm" data-testid="payouts-empty">No matching records. Adjust filters or mark a gig as paid first.</div>}
        {!loading && preview.map((g) => (
          <PayoutRow key={g.id} gig={g} />
        ))}
      </div>
    </div>
  );
}

function PayoutRow({ gig }) {
  const [creative, setCreative] = useState(null);
  useEffect(() => {
    if (!gig.assigned_creative_id) return;
    api.get("/admin/roster").then(({ data }) => {
      setCreative((data || []).find((u) => u.id === gig.assigned_creative_id));
    }).catch(() => {});
  }, [gig.assigned_creative_id]);
  const bank = creative?.bank_details || {};
  return (
    <div data-testid={`payout-row-${gig.id}`} className="grid grid-cols-12 border-t border-[#122E1C] hover:bg-[#0D2114]/40 text-sm min-w-[920px]">
      <div className="p-3 col-span-2 font-mono-jb text-xs text-[#8A9F90]">{(gig.payout_marked_at || "").slice(0, 10) || "—"}</div>
      <div className="p-3 col-span-3">
        <div className="font-semibold truncate">{gig.title}</div>
        <div className="text-xs text-[#8A9F90]">{gig.client_name}</div>
        {gig.transfer_reference && <div className="text-[10px] text-[#F4B41A] font-mono-jb mt-1">REF: {gig.transfer_reference}</div>}
      </div>
      <div className="p-3 col-span-2">
        <div className="truncate">{creative?.name || "—"}</div>
        <div className="text-xs text-[#8A9F90] truncate">{creative?.phone || ""}</div>
      </div>
      <div className="p-3 col-span-2 text-xs font-mono-jb">
        <div className="truncate">{bank.bank_name || "—"}</div>
        <div className="text-[#8A9F90] truncate">{bank.account_number || ""}</div>
      </div>
      <div className="p-3 col-span-1 text-right font-mono-jb">{nairaFormat(gig.budget_ngn || 0)}</div>
      <div className="p-3 col-span-1 text-right font-mono-jb text-[#00E676] font-bold">{nairaFormat(gig.creative_share_ngn || 0)}</div>
      <div className="p-3 col-span-1 text-right font-mono-jb text-[#F4B41A]">{nairaFormat(gig.agency_share_ngn || 0)}</div>
    </div>
  );
}

function BroadcastForm({ onSent }) {
  const [form, setForm] = useState({ title: "", body: "", external_link: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/broadcast", form);
      setForm({ title: "", body: "", external_link: "" });
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#122E1C] bg-[#07140B] p-8 max-w-2xl">
      <h2 className="font-display font-bold text-2xl tracking-tight">Broadcast to all creatives</h2>
      <p className="text-sm text-[#8A9F90] mt-1 mb-6">Sends an in-app notification. Optionally include a WhatsApp / Telegram group invite link so they can join the live discussion.</p>
      <div className="space-y-4">
        <Input data-testid="broadcast-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" />
        <Textarea data-testid="broadcast-body" placeholder="Message body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" rows={4} />
        <Input data-testid="broadcast-link" placeholder="WhatsApp / Telegram link (optional)" value={form.external_link} onChange={(e) => setForm({ ...form, external_link: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none text-white" />
      </div>
      <Button data-testid="broadcast-send" disabled={busy || !form.title || !form.body} onClick={send} className="mt-6 bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
        <Send className="w-4 h-4 mr-2" /> Send broadcast
      </Button>
    </div>
  );
}

function EditGigButton({ gig, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: gig.title || "",
    brief: gig.brief || "",
    client_name: gig.client_name || "",
    budget_ngn: gig.budget_ngn || 0,
    required_followers: gig.required_followers || 0,
    follower_range_label: gig.follower_range_label || "",
    platforms: gig.platforms || [],
    deadline: gig.deadline || "",
    notes: gig.notes || "",
    is_special: !!gig.is_special,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/gigs/${gig.id}`, form);
      toast.success("Gig updated · creatives notified");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`edit-gig-${gig.id}`} className="text-[#F4B41A] hover:bg-[#F4B41A]/10 p-2" aria-label="Edit gig">
          <Pencil className="w-4 h-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Edit gig</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Title</Label>
              <Input data-testid="edit-gig-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Client</Label>
              <Input data-testid="edit-gig-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Brief</Label>
            <Textarea data-testid="edit-gig-brief" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" rows={4} />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {!form.is_special && (
              <>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Required followers (min)</Label>
                  <Input data-testid="edit-gig-followers" type="number" value={form.required_followers} onChange={(e) => setForm({ ...form, required_followers: Number(e.target.value || 0) })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white font-mono-jb" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Range label</Label>
                  <Input
                    data-testid="edit-gig-range"
                    value={form.follower_range_label}
                    onChange={(e) => {
                      const label = e.target.value;
                      // Auto-sync required_followers to the first number in the label so
                      // the eligibility check always matches what the creative sees.
                      const nums = label.match(/[\d,]+/g);
                      const parsedMin = nums && nums.length ? Number(nums[0].replace(/,/g, "")) : null;
                      setForm({
                        ...form,
                        follower_range_label: label,
                        required_followers: parsedMin && parsedMin > 0 ? parsedMin : form.required_followers,
                      });
                    }}
                    placeholder="e.g. 5,000 – 10,000"
                    className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white font-mono-jb"
                  />
                  <p className="text-[10px] text-[#8A9F90] font-mono-jb mt-1">The first number in this label is used as the minimum reach for eligibility.</p>
                </div>
              </>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Budget (₦)</Label>
              <Input data-testid="edit-gig-budget" type="number" value={form.budget_ngn} onChange={(e) => setForm({ ...form, budget_ngn: Number(e.target.value || 0) })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white font-mono-jb" />
            </div>
            <div className={form.is_special ? "md:col-span-2" : ""}>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Deadline</Label>
              <Input data-testid="edit-gig-deadline" type="date" value={form.deadline || ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white font-mono-jb" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Platforms (comma-separated)</Label>
            <Input data-testid="edit-gig-platforms" value={form.platforms.join(", ")} onChange={(e) => setForm({ ...form, platforms: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="bg-transparent border-[#122E1C] rounded-none mt-2 text-white" />
          </div>
          <label className="flex items-start gap-3 cursor-pointer border border-[#F4B41A]/40 bg-[#F4B41A]/5 p-3 select-none">
            <input type="checkbox" data-testid="edit-gig-is-special" checked={form.is_special} onChange={(e) => setForm({ ...form, is_special: e.target.checked })} className="mt-0.5 accent-[#F4B41A] w-4 h-4" />
            <div className="flex-1">
              <div className="font-mono-jb text-xs uppercase tracking-widest text-[#F4B41A]">★ Special gig</div>
              <div className="text-xs text-[#8A9F90] mt-1">No follower minimum — open to all registered creatives.</div>
            </div>
          </label>
          <div className="border border-[#00E676]/30 bg-[#0D2114] p-3 grid grid-cols-3 gap-3 text-sm font-mono-jb">
            <div><div className="text-[10px] text-[#8A9F90] uppercase tracking-widest">Creative (80%)</div><div className="text-[#00E676] mt-1">{nairaFormat(Math.round((form.budget_ngn || 0) * 0.8))}</div></div>
            <div><div className="text-[10px] text-[#8A9F90] uppercase tracking-widest">Treasury (20%)</div><div className="text-[#F4B41A] mt-1">{nairaFormat(Math.round((form.budget_ngn || 0) * 0.2))}</div></div>
            <div><div className="text-[10px] text-[#8A9F90] uppercase tracking-widest">Gross</div><div className="text-white mt-1">{nairaFormat(form.budget_ngn || 0)}</div></div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="edit-gig-save" disabled={busy || !form.title || !form.brief} onClick={save} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportsPanel() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { period } });
      setData(data);
    } catch (e) {
      toast.error("Failed to load report");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight flex items-center gap-2"><BarChart3 className="w-6 h-6 text-[#F4B41A]" /> Earnings reports</h2>
          <p className="text-sm text-[#8A9F90] mt-1">How much has flowed through Nasarawa Expo — and to how many creators.</p>
        </div>
        <div className="flex gap-1 border border-[#122E1C]">
          {["week", "month", "year", "all"].map((p) => (
            <button
              key={p}
              data-testid={`report-period-${p}`}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-xs font-mono-jb uppercase tracking-widest ${period === p ? "bg-[#F4B41A] text-[#030A06]" : "bg-transparent text-[#8A9F90] hover:text-[#F4B41A]"}`}
            >
              {p === "all" ? "All time" : `Last ${p}`}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="text-[#8A9F90] text-sm">Loading…</div>
      ) : (
        <>
          <div className="grid md:grid-cols-4 gap-px bg-[#122E1C]">
            <div className="bg-[#07140B] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Gigs paid</div>
              <div className="font-mono-jb text-3xl text-white mt-2" data-testid="report-gigs">{data.gig_count}</div>
            </div>
            <div className="bg-[#07140B] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Creators paid</div>
              <div className="font-mono-jb text-3xl text-[#00E676] mt-2" data-testid="report-creators">{data.unique_creatives_paid}</div>
            </div>
            <div className="bg-[#07140B] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">To creators (80%)</div>
              <div className="font-mono-jb text-2xl text-[#00E676] mt-2" data-testid="report-creator-total">{nairaFormat(data.creative_paid_total_ngn)}</div>
            </div>
            <div className="bg-[#07140B] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Treasury (20%)</div>
              <div className="font-mono-jb text-2xl text-[#F4B41A] mt-2" data-testid="report-treasury">{nairaFormat(data.agency_treasury_ngn)}</div>
            </div>
          </div>

          <div className="border border-[#122E1C] bg-[#07140B] p-5">
            <h3 className="font-display font-bold text-lg tracking-tight mb-4">Top earners this period</h3>
            {data.top_earners.length === 0 ? (
              <div className="text-[#8A9F90] text-sm">No paid gigs in this window yet.</div>
            ) : (
              <div className="space-y-2">
                {data.top_earners.map((t, i) => (
                  <div key={t.id} data-testid={`report-top-${i}`} className="flex items-center justify-between border-b border-[#122E1C] py-2">
                    <div className="flex items-center gap-3">
                      <span className="font-mono-jb text-[#F4B41A]">#{i + 1}</span>
                      <span>{t.name}</span>
                    </div>
                    <span className="font-mono-jb text-[#00E676] font-bold">{nairaFormat(t.earned_ngn)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function ApplicantPayoutButton({ gigId, applicant, amount, onDone, hasProof, gig }) {
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);

  // Suggest amount based on relevant-platform followers for this creative.
  // If gig has platforms specified, use those; otherwise fall back to total followers.
  const suggestedAmount = React.useMemo(() => {
    const relevantPlatforms = (gig?.platforms || []).map((p) => p.toLowerCase());
    let followers = 0;
    if (relevantPlatforms.length > 0 && applicant.creative?.social_handles) {
      applicant.creative.social_handles.forEach((h) => {
        if (relevantPlatforms.includes((h.platform || "").toLowerCase())) {
          followers += Number(h.followers || 0);
        }
      });
    }
    if (!followers) followers = Number(applicant.creative?.total_followers || 0);
    return Math.round((followers / 1000) * 500 * 0.8);
  }, [applicant, gig]);

  const [payAmount, setPayAmount] = useState(suggestedAmount || amount || 0);
  React.useEffect(() => { setPayAmount(suggestedAmount || amount || 0); }, [suggestedAmount, amount, open]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/gigs/${gigId}/payout?creative_id=${applicant.creative_id}`, { transfer_reference: ref, amount_ngn: Number(payAmount) || 0 });
      toast.success(`Payout marked for ${applicant.creative?.name}`);
      setOpen(false);
      setRef("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  // Show per-platform breakdown for context
  const platforms = (gig?.platforms || []);
  const relevantHandles = (applicant.creative?.social_handles || []).filter((h) => platforms.length === 0 || platforms.map((p) => p.toLowerCase()).includes((h.platform || "").toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`applicant-payout-${applicant.id}`} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none w-full">
          Mark {nairaFormat(suggestedAmount || amount)} paid
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Confirm payout to {applicant.creative?.name}</DialogTitle></DialogHeader>

        {platforms.length > 0 && (
          <div className="text-xs text-[#8A9F90]">
            <div className="text-[10px] uppercase tracking-widest font-mono-jb text-[#F4B41A] mb-1">Relevant followers · {platforms.join(", ")}</div>
            {relevantHandles.length > 0 ? relevantHandles.map((h, i) => (
              <div key={i} className="font-mono-jb">{h.platform}: <span className="text-[#00E676]">{Number(h.followers || 0).toLocaleString()}</span></div>
            )) : <div className="text-[#FF3B30]">No matching handles on file.</div>}
            <div className="mt-1 text-[10px]">Auto-suggested amount = 80% × (relevant followers ÷ 1000) × ₦500</div>
          </div>
        )}

        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] font-mono-jb">Amount to pay (₦) — editable</Label>
          <Input
            data-testid="applicant-payout-amount"
            type="number"
            min="0"
            value={payAmount}
            onChange={(e) => setPayAmount(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            className="bg-transparent border-[#122E1C] rounded-none text-white font-mono-jb text-xl mt-2"
          />
        </div>

        {!hasProof && (
          <div className="text-xs text-[#F4B41A] border border-[#F4B41A]/40 bg-[#F4B41A]/10 p-2">
            ⚠ This creative hasn't submitted proof of work yet.
          </div>
        )}
        <Input data-testid="applicant-payout-ref" placeholder="Bank transfer reference (optional)" value={ref} onChange={(e) => setRef(e.target.value)} className="bg-transparent border-[#122E1C] rounded-none text-white" />
        <DialogFooter>
          <Button data-testid="applicant-payout-confirm" disabled={busy || !payAmount} onClick={submit} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">Confirm & notify</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const FAKE_FOLLOWER_TEMPLATE = "Your profile has been flagged for follower counts that don't match organic engagement. You won't be eligible for gigs until this is fixed. Please update your handles with real, organic followers (no bought/bot audiences) and Nasarawa Expo will re-verify.";

function NotifyCreativeButton({ creative }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("Please review your profile");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/creatives/${creative.id}/notify`, { title, body });
      toast.success("Sent");
      setOpen(false);
      setBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`notify-creative-${creative.id}`} className="text-[#F4B41A] text-xs underline">Notify</button>
      </DialogTrigger>
      <DialogContent className="bg-[#07140B] border-[#122E1C] text-white rounded-none max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Message {creative.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="notify-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Subject" className="bg-transparent border-[#122E1C] rounded-none text-white" />
          <Textarea data-testid="notify-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message" rows={5} className="bg-transparent border-[#122E1C] rounded-none text-white" />
          <button
            type="button"
            onClick={() => { setTitle("Follower verification failed"); setBody(FAKE_FOLLOWER_TEMPLATE); }}
            data-testid="notify-fake-followers-template"
            className="text-xs text-[#F4B41A] underline font-mono-jb"
          >
            Use "inflated followers" template →
          </button>
        </div>
        <DialogFooter>
          <Button data-testid="notify-send" disabled={busy || !body.trim()} onClick={send} className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
            {busy ? "Sending…" : "Send in-app message"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

